import { type FC, useId, useState } from 'react';

import type { GoalVm } from '@eluve/api-contract';
import { allRowsConfirmed, itemReviewPosture } from '@eluve/api-contract';
import {
  Button,
  Checkbox,
  Icon,
  Input,
  Label,
  Stack,
  Tag,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import type { CarePlanEvidenceSelectionHandler } from './CarePlanEvidenceDrawer';
import { ItemEditForm } from './ItemEditForm';
import { ItemReviewRow } from './ItemReviewRow';
import { InlineChangeDiff, PendingChangeDiff } from './PendingChangeDiff';
import { SectionBulkControls } from './SectionBulkControls';
import { changeByField, confirmableRows } from './proposal-rows';

/** The goal-row fields that render their diff in a dedicated slot (title,
 *  Target row, Status row); anything else falls back to a generic diff line. */
const SLOTTED_FIELDS = ['text', 'targetDate', 'status'];

type GoalEditFields = {
  text: string;
  targetDate: string | null;
  done: boolean;
  isPrimary: boolean;
};

const GoalEditForm: FC<{
  initial: GoalEditFields;
  disabled?: boolean;
  onCancel: () => void;
  onSave: (fields: GoalEditFields) => void;
  onDelete?: () => void;
}> = ({ initial, disabled, onCancel, onSave, onDelete }) => {
  const { t } = useTranslation();
  const [text, setText] = useState(initial.text);
  const [targetDate, setTargetDate] = useState(initial.targetDate ?? '');
  const [done, setDone] = useState(initial.done);
  const [isPrimary, setIsPrimary] = useState(initial.isPrimary);
  const doneId = useId();
  const primaryId = useId();
  const isEdit = Boolean(onDelete);

  return (
    <ItemEditForm
      title={
        isEdit ? t('carePlans.goals.editGoal') : t('carePlans.goals.addGoal')
      }
      saveLabel={
        isEdit ? t('carePlans.review.saveChanges') : t('carePlans.review.save')
      }
      saveDisabled={text.trim().length === 0}
      disabled={disabled}
      onCancel={onCancel}
      onSave={() =>
        onSave({
          text: text.trim(),
          targetDate: targetDate || null,
          done,
          isPrimary,
        })
      }
      onDelete={onDelete}
      deleteLabel={t('carePlans.goals.deleteGoal')}
      deleteConfirmTitle={t('carePlans.goals.deleteConfirmTitle')}
      deleteConfirmBody={t('carePlans.goals.deleteConfirmBody')}
    >
      <Stack direction="vertical" gap={3} className="mt-3">
        <Input
          value={text}
          disabled={disabled}
          placeholder={t('carePlans.goals.textPlaceholder')}
          onChange={(e) => setText(e.target.value)}
          aria-label={t('carePlans.goals.textAriaLabel')}
        />
        <Stack direction="vertical" gap={1}>
          <Text size="xs" color="secondary">
            {t('carePlans.goals.targetDate')}
          </Text>
          <Input
            type="date"
            value={targetDate}
            disabled={disabled}
            onChange={(e) => setTargetDate(e.target.value)}
            className="w-44"
            aria-label={t('carePlans.goals.targetDateAriaLabel')}
          />
        </Stack>
        <Stack direction="horizontal" align="center" gap={2}>
          <Checkbox
            id={doneId}
            checked={done}
            disabled={disabled}
            onCheckedChange={(c) => setDone(c === true)}
          />
          <Label htmlFor={doneId} className="cursor-pointer font-normal">
            {t('carePlans.goals.markDone')}
          </Label>
        </Stack>
        <Stack direction="horizontal" align="start" gap={2}>
          <Checkbox
            id={primaryId}
            checked={isPrimary}
            disabled={disabled}
            onCheckedChange={(c) => setIsPrimary(c === true)}
            className="mt-0.5"
          />
          <Stack direction="vertical" gap={0}>
            <Label htmlFor={primaryId} className="cursor-pointer font-normal">
              {t('carePlans.goals.markPrimary')}
            </Label>
            <Text size="xs" color="secondary">
              {t('carePlans.goals.primaryHint')}
            </Text>
          </Stack>
        </Stack>
      </Stack>
    </ItemEditForm>
  );
};

export const GoalsSection: FC<{
  goals: GoalVm[];
  disabled?: boolean;
  showBulkUndo?: boolean;
  onConfirm: (goal: GoalVm) => void;
  onConfirmAll?: (goals: GoalVm[]) => void;
  onUndoConfirmAll?: () => void;
  onReject: (goal: GoalVm) => void;
  onUndo: (goal: GoalVm) => void;
  onEditGoal: (goal: GoalVm, fields: GoalEditFields) => void;
  onDelete: (goal: GoalVm) => void;
  onAddGoal: (fields: GoalEditFields) => void;
  onSelectEvidence?: CarePlanEvidenceSelectionHandler;
}> = ({
  goals,
  disabled,
  showBulkUndo,
  onConfirm,
  onConfirmAll,
  onUndoConfirmAll,
  onReject,
  onUndo,
  onEditGoal,
  onDelete,
  onAddGoal,
  onSelectEvidence,
}) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const confirmable = confirmableRows(goals);
  const allConfirmed = allRowsConfirmed(goals.map(itemReviewPosture));

  return (
    <section className="w-full rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
      <SectionBulkControls
        title={t('carePlans.goals.title')}
        confirmableCount={confirmable.length}
        allConfirmed={allConfirmed}
        showUndoAll={Boolean(showBulkUndo)}
        disabled={disabled}
        onConfirmAll={() =>
          onConfirmAll
            ? onConfirmAll(confirmable)
            : confirmable.forEach(onConfirm)
        }
        onUndoAll={onUndoConfirmAll}
      />

      <Stack direction="vertical" gap={2} className="mt-3">
        {goals.map((goal) => {
          const textChange = changeByField(goal, 'text');
          const evidenceLabel = textChange?.proposed ?? goal.text;
          const sourceEvidence = goal.sourceEvidence;
          const dateChange = changeByField(goal, 'targetDate');
          const statusChange = changeByField(goal, 'status');
          const otherChanges = goal.pendingChanges.filter(
            (change) =>
              change.decision === 'pending' &&
              !SLOTTED_FIELDS.includes(change.field),
          );
          return editingId === goal.id ? (
            <GoalEditForm
              key={goal.id}
              initial={{
                text: goal.text,
                targetDate: goal.targetDate,
                done: goal.done,
                isPrimary: goal.isPrimary,
              }}
              disabled={disabled}
              onCancel={() => setEditingId(null)}
              onSave={(fields) => {
                onEditGoal(goal, fields);
                setEditingId(null);
              }}
              onDelete={() => {
                onDelete(goal);
                setEditingId(null);
              }}
            />
          ) : (
            <ItemReviewRow
              key={goal.id}
              itemState={goal.itemState}
              decision={goal.decision}
              title={
                <Stack direction="horizontal" align="center" gap={2}>
                  {textChange ? (
                    <Stack direction="vertical" gap={0.5} fullWidth={false}>
                      <Text inline color="secondary" className="line-through">
                        {textChange.previous ?? goal.text}
                      </Text>
                      <Text inline className="text-text-insights">
                        → {textChange.proposed}
                      </Text>
                    </Stack>
                  ) : (
                    <Text inline>{goal.text}</Text>
                  )}
                  {goal.isPrimary && (
                    <Tag
                      color="purple"
                      subtle
                      pill
                      size="xs"
                      label={t('carePlans.goals.primary')}
                    />
                  )}
                </Stack>
              }
              disabled={disabled}
              sourceLabel={
                sourceEvidence && onSelectEvidence ? evidenceLabel : undefined
              }
              onSelectSource={
                sourceEvidence && onSelectEvidence
                  ? (triggerElement) =>
                      onSelectEvidence(
                        {
                          label: evidenceLabel,
                          itemType: 'goal',
                          evidence: sourceEvidence,
                        },
                        triggerElement,
                      )
                  : undefined
              }
              onConfirm={() => onConfirm(goal)}
              onReject={() => onReject(goal)}
              onUndo={() => onUndo(goal)}
              onEdit={() => setEditingId(goal.id)}
            >
              <Stack direction="vertical" gap={0.5} className="mt-2">
                <Stack direction="horizontal" gap={3}>
                  <Text size="xs" color="secondary" className="w-12">
                    {t('carePlans.goals.target')}
                  </Text>
                  {dateChange ? (
                    <InlineChangeDiff
                      previous={dateChange.previous}
                      proposed={dateChange.proposed}
                    />
                  ) : (
                    <Text size="sm">
                      {goal.targetDate ?? t('carePlans.review.notSet')}
                    </Text>
                  )}
                </Stack>
                <Stack direction="horizontal" gap={3}>
                  <Text size="xs" color="secondary" className="w-12">
                    {t('carePlans.goals.status')}
                  </Text>
                  {statusChange ? (
                    <InlineChangeDiff
                      previous={statusChange.previous}
                      proposed={statusChange.proposed}
                    />
                  ) : (
                    <Text
                      size="sm"
                      className={goal.done ? 'text-text-positive' : undefined}
                    >
                      {goal.done
                        ? t('carePlans.goals.done')
                        : t('carePlans.goals.notDone')}
                    </Text>
                  )}
                </Stack>
                {otherChanges.map((change) => (
                  <PendingChangeDiff key={change.proposalId} change={change} />
                ))}
              </Stack>
            </ItemReviewRow>
          );
        })}
      </Stack>

      {adding ? (
        <div className="mt-3">
          <GoalEditForm
            initial={{
              text: '',
              targetDate: null,
              done: false,
              isPrimary: false,
            }}
            disabled={disabled}
            onCancel={() => setAdding(false)}
            onSave={(fields) => {
              onAddGoal(fields);
              setAdding(false);
            }}
          />
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => setAdding(true)}
          className="mt-3"
        >
          <Icon name="Plus" size="auto" />
          {t('carePlans.goals.addGoal')}
        </Button>
      )}
    </section>
  );
};
